// src/lib/marketing/agregacaoDiaria.ts
import type { MarketingMetaDailyMetric } from '../../../types';

export interface PontoDiarioMarketing {
  date: string;
  spend: number;
  impressions: number;
  link_clicks: number;
  conversations: number;
  leads: number;
  landingPageViews: number;
  videoViews: number;
  results_total: number;
  cost_per_result: number;
  cpm: number;
}

export function computeByDay(filtered: MarketingMetaDailyMetric[]): PontoDiarioMarketing[] {
  const map = new Map<string, {
    spend: number; impressions: number; linkClicks: number;
    conversations: number; leads: number; landingPageViews: number;
    videoViews: number; resultsTotal: number; cpm_sum: number; cpm_count: number;
  }>();
  for (const m of filtered) {
    const cur = map.get(m.date) ?? { spend:0, impressions:0, linkClicks:0, conversations:0, leads:0, landingPageViews:0, videoViews:0, resultsTotal:0, cpm_sum:0, cpm_count:0 };
    cur.spend            += Number(m.spend) || 0;
    cur.impressions      += Number(m.impressions) || 0;
    cur.linkClicks       += Number(m.link_clicks) || 0;
    cur.conversations    += Number(m.conversations_started) || 0;
    cur.leads            += Number(m.leads) || 0;
    cur.landingPageViews += Number(m.landing_page_views) || 0;
    cur.videoViews       += Number(m.video_views) || 0;
    cur.resultsTotal     += Number(m.results_total) || 0;
    if (m.cpm > 0) { cur.cpm_sum += Number(m.cpm); cur.cpm_count++; }
    map.set(m.date, cur);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, v]) => ({
    date,
    spend:            parseFloat(v.spend.toFixed(2)),
    impressions:      v.impressions,
    link_clicks:      v.linkClicks,
    conversations:    v.conversations,
    leads:            v.leads,
    landingPageViews: v.landingPageViews,
    videoViews:       v.videoViews,
    results_total:    v.resultsTotal,
    cost_per_result:  v.resultsTotal > 0 ? parseFloat((v.spend / v.resultsTotal).toFixed(2)) : 0,
    cpm: v.cpm_count > 0 ? parseFloat((v.cpm_sum / v.cpm_count).toFixed(2)) : 0,
  }));
}

export interface KpisMarketing {
  totalSpend: number;
  totalResults: number;
  totalConversations: number;
  totalLeads: number;
  totalLandingViews: number;
  totalImpressions: number;
  totalLinkClicks: number;
  avgCpm: number;
  avgCostPerResult: number;
}

export function aggregateKpis(byDay: PontoDiarioMarketing[]): KpisMarketing {
  const totalSpend         = byDay.reduce((s, d) => s + d.spend, 0);
  const totalResults       = byDay.reduce((s, d) => s + d.results_total, 0);
  const totalConversations = byDay.reduce((s, d) => s + d.conversations, 0);
  const totalLeads         = byDay.reduce((s, d) => s + d.leads, 0);
  const totalLandingViews  = byDay.reduce((s, d) => s + d.landingPageViews, 0);
  const totalImpressions   = byDay.reduce((s, d) => s + d.impressions, 0);
  const totalLinkClicks    = byDay.reduce((s, d) => s + d.link_clicks, 0);
  const avgCpm             = byDay.filter(d => d.cpm > 0).reduce((s, d, _, a) => s + d.cpm / a.length, 0);
  const avgCostPerResult   = totalResults > 0 ? totalSpend / totalResults : 0;
  return { totalSpend, totalResults, totalConversations, totalLeads, totalLandingViews, totalImpressions, totalLinkClicks, avgCpm, avgCostPerResult };
}
